import { z } from 'zod';

export function createOffsetPaginationRequestQuerySchema<
  OrderBySchema extends [string, ...string[]],
>(orderBy: z.ZodEnum<OrderBySchema>) {
  return z
    .object({
      pageSize: z.coerce
        .number()
        .int()
        .positive()
        .max(1000)
        .default(10)
        .optional()
        .describe('Number of items per page (1-1000, default: 10)'),
      page: z.coerce
        .number()
        .int()
        .positive()
        .default(1)
        .optional()
        .describe('Page number to retrieve (1-based, default: 1)'),
      orderBy: orderBy.optional().describe('Field to sort by'),
      orderDirection: z
        .enum(['asc', 'desc'])
        .default('asc')
        .optional()
        .describe('Sort direction (asc or desc, default: asc)'),
    })
    .describe('Query parameters for offset-based pagination');
}

export type OffsetPaginationRequestQuery<OrderByKeys extends string> = Partial<{
  pageSize: number;
  page: number;
  orderBy: OrderByKeys;
  orderDirection: 'asc' | 'desc';
}>;

export function createKeysetPaginationRequestQuerySchema<
  OrderBySchema extends [string, ...string[]],
>(orderBy: z.ZodEnum<OrderBySchema>) {
  return z
    .object({
      orderBy: orderBy.optional().describe('Field to sort by'),
      orderDirection: z
        .enum(['asc', 'desc'])
        .optional()
        .describe('Sort direction (asc or desc)'),
      limit: z.coerce
        .number()
        .optional()
        .describe('Maximum number of items to return'),
      before: z
        .string()
        .optional()
        .describe('Cursor to paginate before (for previous page)'),
      after: z
        .string()
        .optional()
        .describe('Cursor to paginate after (for next page)'),
    })
    .describe('Query parameters for cursor-based pagination');
}

export type KeysetPaginationRequestQuery<OrderByKeys extends string> = Partial<{
  orderBy: OrderByKeys;
  orderDirection: 'asc' | 'desc';
  before: string;
  after: string;
}>;

// Offset Pagination Response
export const OffsetPaginationInfoSchema = z
  .object({
    page: z.number().int().positive().describe('Current page number (1-based)'),
    pageSize: z.number().int().positive().describe('Number of items per page'),
    totalItems: z
      .number()
      .int()
      .nonnegative()
      .describe('Total number of items across all pages'),
    totalPages: z
      .number()
      .int()
      .nonnegative()
      .describe('Total number of pages'),
    hasNextPage: z.boolean().describe('Whether there is a next page available'),
    hasPreviousPage: z
      .boolean()
      .describe('Whether there is a previous page available'),
  })
  .describe('Pagination metadata for offset-based pagination');

export type OffsetPaginationInfo = z.infer<typeof OffsetPaginationInfoSchema>;

export function createOffsetPaginationResponseSchema<T extends z.ZodTypeAny>(
  itemsSchema: T,
) {
  return z
    .object({
      items: z.array(itemsSchema).describe('Array of items for current page'),
      pagination: OffsetPaginationInfoSchema,
    })
    .describe('Response structure for offset-based paginated data');
}

// Keyset/Cursor Pagination Response
export const KeysetPaginationInfoSchema = z
  .object({
    hasMore: z.boolean().describe('Whether there are more items available'),
    nextCursor: z
      .string()
      .nullable()
      .describe('Cursor for next page (null if no more items)'),
    previousCursor: z
      .string()
      .nullable()
      .describe('Cursor for previous page (null if at beginning)'),
    limit: z
      .number()
      .int()
      .positive()
      .describe('Maximum number of items per page'),
  })
  .describe('Pagination metadata for cursor-based pagination');

export type KeysetPaginationInfo = z.infer<typeof KeysetPaginationInfoSchema>;

export function createKeysetPaginationResponseSchema<T extends z.ZodTypeAny>(
  itemsSchema: T,
) {
  return z
    .object({
      items: z.array(itemsSchema).describe('Array of items for current page'),
      pagination: KeysetPaginationInfoSchema,
    })
    .describe('Response structure for cursor-based paginated data');
}

// Helper functions to create pagination info
export function createOffsetPaginationInfo({
  page,
  pageSize,
  totalItems,
}: {
  page: number;
  pageSize: number;
  totalItems: number;
}): OffsetPaginationInfo {
  const totalPages = Math.ceil(totalItems / pageSize);
  return {
    page,
    pageSize,
    totalItems,
    totalPages,
    hasNextPage: page < totalPages,
    hasPreviousPage: page > 1,
  };
}
